import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import DragIcon from 'mdi-react/DragIcon';
import PlusIcon from 'mdi-react/PlusIcon';
import MinusBoxOutlineIcon from 'mdi-react/MinusBoxOutlineIcon';
import PencilOutlineIcon from 'mdi-react/PencilOutlineIcon';
import DeleteOutlineIcon from 'mdi-react/DeleteOutlineIcon';
import ShapeOutlineIcon from 'mdi-react/ShapeOutlineIcon';
import LinkVariantIcon from 'mdi-react/LinkVariantIcon';
import FormatListBulletedIcon from 'mdi-react/FormatListBulletedIcon';
import ViewGridIcon from 'mdi-react/ViewGridIcon';
import LockOutlineIcon from 'mdi-react/LockOutlineIcon';
import WalletMembershipIcon from 'mdi-react/WalletMembershipIcon';
import MagnifyIcon from 'mdi-react/MagnifyIcon';
import { partial } from '../../util/translation/translation';
import FormCard from '../../shared/components/custom/FormCard';
import InfoBox from '../../shared/components/custom/InfoBox';
import SquareIconButton from '../../shared/components/custom/SquareIconButton';

// Droppable ids: the cards for the club's own entries, the app's components
// not in the menu, and the menu itself (Col0/Col1 as in the page's state)
export const PALETTE = 'MorePalette';
export const PALETTE_LINK = 0;
export const PALETTE_DIVIDER = 1;

const isLink = (item) => !!item?.link;
const isDivider = (item) => !!item?.divider;

/**
 * The More menu tab of App layout: on the left what can go in the menu (a
 * link or divider of the club's own, and the app's components not in it),
 * dragged in where it should go or added at the end with +; in the middle
 * the menu, with My memberships pinned first; on the right the preview.
 */
const MoreMenuEditor = ({
  more,
  onDragEnd,
  renderIcon,
  isDisabled,
  onAdd,
  onRemove,
  onAddLink,
  onAddDivider,
  onEditLink,
  onEditDivider,
  onChangeIcon,
  onToggleLayout,
  memberRowShown,
  onToggleMemberRow,
  preview,
}) => {
  const p = partial('appLayout');
  const s = partial('shared');
  const [search, setSearch] = useState('');
  const available = more.Col0?.items || [];
  const menu = more.Col1?.items || [];
  const query = search.trim().toLowerCase();
  const shown = query
    ? available.filter((item) =>
        String(item.name).toLowerCase().includes(query)
      )
    : available;

  const palette = [
    {
      key: 'link',
      icon: <LinkVariantIcon size={18} />,
      label: p('moreLinkCard'),
      hint: p('moreLinkCardHint'),
      addLabel: p('moreLinkAddEnd'),
      add: onAddLink,
    },
    {
      key: 'divider',
      icon: <FormatListBulletedIcon size={18} />,
      label: p('divider'),
      hint: p('moreDividerCardHint'),
      addLabel: p('moreDividerAddEnd'),
      add: onAddDivider,
    },
  ];

  const renderDivider = (item, provided, snapshot) => {
    const grid = item.divider.layout === 'grid';
    return (
      <div
        ref={provided.innerRef}
        {...provided.draggableProps}
        {...provided.dragHandleProps}
        className={`more-editor__row more-editor__row--divider${
          snapshot.isDragging ? ' more-editor__row--dragging' : ''
        }`}
        style={provided.draggableProps.style}
      >
        <DragIcon size={18} className="more-editor__grip" />
        <span
          className={`more-divider__title${
            item.divider.title ? '' : ' more-divider__title--empty'
          }`}
        >
          {item.divider.title || p('divider')}
        </span>
        <span className="more-divider__rule" />
        <button
          type="button"
          className="more-editor__layout"
          onClick={() => onToggleLayout(item)}
          title={p('dividerLayout')}
        >
          {grid ? (
            <ViewGridIcon size={14} />
          ) : (
            <FormatListBulletedIcon size={14} />
          )}
          {grid ? p('dividerGridShort') : p('dividerList')}
        </button>
        <div className="icon-actions more-item__actions">
          <SquareIconButton
            icon={<PencilOutlineIcon />}
            title={p('editDivider')}
            onClick={() => onEditDivider(item)}
          />
          <SquareIconButton
            icon={<DeleteOutlineIcon />}
            title={s('delete')}
            tone="danger"
            onClick={() => onRemove(item)}
          />
        </div>
      </div>
    );
  };

  const renderEntry = (item, provided, snapshot) => (
    <div
      ref={provided.innerRef}
      {...provided.draggableProps}
      {...provided.dragHandleProps}
      className={`more-editor__row${
        snapshot.isDragging ? ' more-editor__row--dragging' : ''
      }${isDisabled(item) ? ' more-editor__row--disabled' : ''}`}
      style={provided.draggableProps.style}
    >
      <DragIcon size={18} className="more-editor__grip" />
      <span className="more-editor__icon more-editor__icon--menu">
        {renderIcon(item)}
      </span>
      <span className="more-editor__text">
        <span className="more-editor__name">{item.name}</span>
        {isLink(item) && (
          <span className="more-editor__sub">
            {p('moreLinkKind', {
              type: p(`moreLinkKind-${item.link.contentType}`),
            })}
          </span>
        )}
      </span>
      <div className="icon-actions more-item__actions">
        {isLink(item) ? (
          <>
            <SquareIconButton
              icon={<PencilOutlineIcon />}
              title={s('edit')}
              onClick={() => onEditLink(item)}
            />
            <SquareIconButton
              icon={<DeleteOutlineIcon />}
              title={s('delete')}
              tone="danger"
              onClick={() => onRemove(item)}
            />
          </>
        ) : (
          <>
            <SquareIconButton
              icon={<ShapeOutlineIcon />}
              title={p('iconChange')}
              onClick={() => onChangeIcon(item)}
            />
            <SquareIconButton
              icon={<MinusBoxOutlineIcon />}
              title={p('moreRemoveItem')}
              onClick={() => onRemove(item)}
            />
          </>
        )}
      </div>
    </div>
  );

  // While searching, the list shows only some of the components: an index
  // in it is turned back into the component's place among all of them
  const handleDragEnd = (result) => {
    if (result.source.droppableId !== 'Col0') {
      onDragEnd(result);
      return;
    }
    const index = available.indexOf(shown[result.source.index]);
    onDragEnd({ ...result, source: { ...result.source, index } });
  };

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <div className="more-editor">
        <FormCard
          className="more-editor__add"
          title={
            <span className="more-editor__heading">
              {p('moreAddTitle')}
              <span className="more-editor__heading-hint">
                {p('moreAddHint')}
              </span>
            </span>
          }
        >
          <span className="more-editor__label">{p('moreYourOwn')}</span>
          <Droppable droppableId={PALETTE} isDropDisabled>
            {(provided) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className="more-editor__palette"
              >
                {palette.map((card, index) => (
                  <Draggable
                    key={card.key}
                    draggableId={`palette-${card.key}`}
                    index={index}
                  >
                    {(dragProvided, snapshot) => (
                      <div
                        ref={dragProvided.innerRef}
                        {...dragProvided.draggableProps}
                        {...dragProvided.dragHandleProps}
                        className={`more-editor__card${
                          snapshot.isDragging
                            ? ' more-editor__card--dragging'
                            : ''
                        }`}
                        style={dragProvided.draggableProps.style}
                      >
                        <DragIcon size={18} className="more-editor__grip" />
                        <span className="more-editor__icon more-editor__icon--own">
                          {card.icon}
                        </span>
                        <span className="more-editor__text">
                          <span className="more-editor__name">
                            {card.label}
                          </span>
                          <span className="more-editor__sub">{card.hint}</span>
                        </span>
                        <SquareIconButton
                          icon={<PlusIcon />}
                          title={card.addLabel}
                          onClick={() => card.add()}
                        />
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>

          <div className="more-editor__label-row">
            <span className="more-editor__label">{p('moreFromApp')}</span>
            <span className="more-editor__count">
              {p('moreNotInMenu', { count: available.length })}
            </span>
          </div>
          <label htmlFor="more-editor-search" className="more-editor__search">
            <MagnifyIcon size={16} />
            <input
              id="more-editor-search"
              type="search"
              value={search}
              placeholder={p('searchComponents')}
              aria-label={p('searchComponents')}
              onChange={(e) => setSearch(e.target.value)}
            />
          </label>
          <Droppable droppableId="Col0">
            {(provided, snapshot) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className={`more-editor__available${
                  snapshot.isDraggingOver ? ' more-editor__available--over' : ''
                }`}
              >
                {shown.map((item, index) => (
                  <Draggable
                    key={item.id}
                    draggableId={`${item.id}Col0`}
                    index={index}
                    isDragDisabled={isDisabled(item)}
                  >
                    {(dragProvided, dragSnapshot) => (
                      <div
                        ref={dragProvided.innerRef}
                        {...dragProvided.draggableProps}
                        {...dragProvided.dragHandleProps}
                        className={`more-editor__option${
                          dragSnapshot.isDragging
                            ? ' more-editor__option--dragging'
                            : ''
                        }${
                          isDisabled(item)
                            ? ' more-editor__option--disabled'
                            : ''
                        }`}
                        style={dragProvided.draggableProps.style}
                      >
                        <DragIcon size={18} className="more-editor__grip" />
                        <span className="more-editor__icon">
                          {renderIcon(item)}
                        </span>
                        <span className="more-editor__name">{item.name}</span>
                        <SquareIconButton
                          icon={<PlusIcon />}
                          title={p('moreAddItem', { name: item.name })}
                          onClick={() => onAdd(item)}
                          disabled={isDisabled(item)}
                        />
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
                {available.length === 0 && (
                  <p className="more-editor__empty">{p('moreAllUsed')}</p>
                )}
                {available.length > 0 && shown.length === 0 && (
                  <p className="more-editor__empty">{p('noComponentsMatch')}</p>
                )}
              </div>
            )}
          </Droppable>
        </FormCard>

        <FormCard
          className="more-editor__menu"
          bodyClassName="more-editor__menu-body"
          title={p('more')}
          actions={
            <span className="more-editor__heading-hint">
              {p('dragToReorder')}
            </span>
          }
        >
          <div className="more-editor__pinned">
            <LockOutlineIcon
              size={18}
              className="more-editor__lock"
              aria-label={p('memberRowPinned')}
            />
            <span className="more-editor__icon more-editor__icon--member">
              <WalletMembershipIcon size={18} />
            </span>
            <span className="more-editor__text">
              <span className="more-editor__name">{p('memberRowName')}</span>
              <span className="more-editor__sub">
                {memberRowShown
                  ? p('memberRowShownHint')
                  : p('memberRowHiddenHint')}
              </span>
            </span>
            <span className="more-editor__pin">{p('memberRowPinned')}</span>
            <button
              type="button"
              role="switch"
              aria-checked={memberRowShown}
              aria-label={p('memberRowSwitch')}
              className={`more-editor__switch${
                memberRowShown ? ' more-editor__switch--on' : ''
              }`}
              onClick={onToggleMemberRow}
            />
          </div>
          <Droppable droppableId="Col1">
            {(provided, snapshot) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className={`more-editor__list${
                  snapshot.isDraggingOver ? ' more-editor__list--over' : ''
                }`}
              >
                {menu.map((item, index) => (
                  <Draggable
                    key={item.id}
                    draggableId={`${item.id}Col1`}
                    index={index}
                  >
                    {(dragProvided, dragSnapshot) =>
                      isDivider(item)
                        ? renderDivider(item, dragProvided, dragSnapshot)
                        : renderEntry(item, dragProvided, dragSnapshot)
                    }
                  </Draggable>
                ))}
                {provided.placeholder}
                {menu.length === 0 && (
                  <p className="more-editor__empty more-editor__empty--menu">
                    {p('moreMenuEmpty')}
                  </p>
                )}
              </div>
            )}
          </Droppable>
          <InfoBox className="more-editor__info">
            <strong>{p('moreAccountLastTitle')}</strong>{' '}
            {p('moreAccountLastText')}
          </InfoBox>
        </FormCard>

        <div className="more-editor__preview">{preview}</div>
      </div>
    </DragDropContext>
  );
};

MoreMenuEditor.propTypes = {
  more: PropTypes.shape({
    Col0: PropTypes.shape({ items: PropTypes.arrayOf(PropTypes.object) }),
    Col1: PropTypes.shape({ items: PropTypes.arrayOf(PropTypes.object) }),
  }).isRequired,
  onDragEnd: PropTypes.func.isRequired,
  renderIcon: PropTypes.func.isRequired,
  isDisabled: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onAddLink: PropTypes.func.isRequired,
  onAddDivider: PropTypes.func.isRequired,
  onEditLink: PropTypes.func.isRequired,
  onEditDivider: PropTypes.func.isRequired,
  onChangeIcon: PropTypes.func.isRequired,
  onToggleLayout: PropTypes.func.isRequired,
  memberRowShown: PropTypes.bool.isRequired,
  onToggleMemberRow: PropTypes.func.isRequired,
  preview: PropTypes.node,
};

MoreMenuEditor.defaultProps = {
  preview: null,
};

export default MoreMenuEditor;
